/**
 * Hub i18n Nestor — Phase 8C (split par langue + lazy loading).
 *
 * Historique : jusqu'à la Phase 8A, les 7 dictionnaires vivaient ici
 * en un seul module (~99 KB gzip de bundle). Depuis 8C :
 *
 *   - `locales/fr.ts` (source de vérité des clés) et `locales/en.ts`
 *     sont **embarqués** dans le bundle principal.
 *   - `es/de/pt/it/pl` sont des **chunks séparés** chargés à la
 *     demande (`loadDictionary`) au premier `setLang` vers cette
 *     langue. Pendant le chargement, `t()` sert le fallback FR ;
 *     les vues se re-rendent à l'arrivée du dictionnaire via la
 *     notification `subscribeLang` (cf. `lang.ts`).
 *
 * Les traductions elles-mêmes n'ont pas bougé (cf. ADR 0012 et le
 * test de parité `i18n-parity.test.ts`).
 */
import { fr, type Dictionary, type TKey } from './locales/fr.js';
import { en } from './locales/en.js';

export { fr };
export type { Dictionary, TKey };

export const SUPPORTED_LANGS = ['fr', 'en', 'es', 'de', 'pt', 'it', 'pl'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

export const DEFAULT_LANG: Lang = 'fr';

/** Langues embarquées dans le bundle principal. */
type StaticLang = 'fr' | 'en';
type LazyLang = Exclude<Lang, StaticLang>;

// Registre runtime des dictionnaires disponibles. FR + EN d'entrée ;
// les autres langues y sont posées à la résolution de leur import().
const registry: Partial<Record<Lang, Dictionary>> = { fr, en };

const LOADERS: Record<LazyLang, () => Promise<Dictionary>> = {
  es: async () => (await import('./locales/es.js')).es,
  de: async () => (await import('./locales/de.js')).de,
  pt: async () => (await import('./locales/pt.js')).pt,
  it: async () => (await import('./locales/it.js')).it,
  pl: async () => (await import('./locales/pl.js')).pl,
};

/** Dictionnaire d'une langue s'il est déjà disponible (sync). */
export function getDictionary(lang: Lang): Dictionary | undefined {
  return registry[lang];
}

/**
 * Charge (si besoin) le dictionnaire d'une langue. Idempotent : les
 * langues statiques et les langues déjà chargées résolvent
 * immédiatement. Peut rejeter sur erreur réseau (chunk inaccessible) —
 * l'appelant décide ; `t()` continue de servir le fallback FR.
 */
export async function loadDictionary(lang: Lang): Promise<Dictionary> {
  const existing = registry[lang];
  if (existing) return existing;
  const dict = await LOADERS[lang as LazyLang]();
  registry[lang] = dict;
  return dict;
}

/** @internal pour tests — vide le registre des langues lazy. */
export function _clearDictionaryRegistryForTests(): void {
  for (const key of Object.keys(registry) as Lang[]) {
    if (key !== 'fr' && key !== 'en') delete registry[key];
  }
}
